/**
 * Service card component
 */
import { Service } from '@/types';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ProtectedAction } from '@/components/shared/ProtectedAction';
import { canManageService } from '@/lib/permissions';

interface ServiceCardProps {
  service: Service;
  onEdit?: (serviceId: string) => void;
  onDelete?: (serviceId: string) => void;
}

export function ServiceCard({ service, onEdit, onDelete }: ServiceCardProps) {
  const formatPrice = (cents: number) => {
    return `€${(cents / 100).toFixed(2)}`;
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  };

  const getCategoryIcon = (category: string) => {
    const icons: Record<string, string> = {
      Haircut: '✂️',
      Beard: '🧔',
      Shave: '🪒',
      Styling: '💇',
      Color: '🎨',
      Treatment: '💆',
      Kids: '👶',
      Special: '⭐',
      Package: '📦',
      Other: '✨',
    };
    return icons[category] || '✂️';
  };

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow">
      {/* Service Image */}
      {service.imageUrl ? (
        <div className="relative w-full h-48 bg-surface-2">
          <img
            src={service.imageUrl}
            alt={service.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const fallback = e.currentTarget.nextElementSibling as HTMLElement;
              if (fallback) fallback.style.display = 'flex';
            }}
          />
          <div className="hidden w-full h-full items-center justify-center text-6xl">
            {getCategoryIcon(service.category)}
          </div>
        </div>
      ) : (
        <div className="w-full h-48 flex items-center justify-center">
          <span className="text-6xl">{getCategoryIcon(service.category)}</span>
        </div>
      )}

      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-ink">{service.name}</h3>
            <span className="inline-block mt-1 px-2 py-1 bg-accent/10 text-accent text-xs rounded-full">
              {service.category}
            </span>
          </div>
        </div>

      {service.description && (
        <p className="text-sm text-ink-muted mb-4 line-clamp-2">{service.description}</p>
      )}

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1 text-ink-muted">
            <span>⏱️</span>
            <span>{formatDuration(service.duration)}</span>
          </div>
          <div className="flex items-center gap-1 font-semibold text-ink">
            <span>💰</span>
            <span>{formatPrice(service.price)}</span>
          </div>
        </div>
      </div>

        <ProtectedAction requiredPermission={(claims) => canManageService(claims, service.franchiseId)}>
          <div className="flex gap-2 pt-4 border-t border-hairline">
            {onEdit && (
              <Button size="sm" variant="ghost" onClick={() => onEdit(service.serviceId)} className="flex-1">
                Edit
              </Button>
            )}
            {onDelete && (
              <Button size="sm" variant="danger" onClick={() => onDelete(service.serviceId)} className="flex-1">
                Delete
              </Button>
            )}
          </div>
        </ProtectedAction>
      </div>
    </Card>
  );
}
